// LearningPage.jsx — Staff L&D dashboard with 4 tabs.
import React, { useCallback, useContext, useEffect, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "../../components/ui/Toast";
import {
  GraduationCap, Award, BookOpen, Sparkles, Upload, X,
  Clock, CheckCircle2, Download, ChevronRight, Search,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON  = "#00FF99";
const AMBER = "#EF9F27";
const BLUE  = "#378ADD";
const PURP  = "#7F77DD";
const RED   = "#E24B4A";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const TABS = [
  { key: "my",      label: "My Learning", Icon: GraduationCap },
  { key: "library", label: "Course Library", Icon: BookOpen },
  { key: "certs",   label: "Certificates", Icon: Award },
  { key: "skills",  label: "Skills", Icon: Sparkles },
];

export default function LearningPage({ embedded = false }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tab, setTab] = useState("my");
  const isManager = ["manager", "admin", "executive", "superadmin"].includes(user?.role);

  const inner = (
    <div className={embedded ? "" : "p-6 space-y-5 text-white"}>
      {!embedded && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={iconBox}><GraduationCap size={16} /></div>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0, color: "#fff" }}>
              Learning & Development
            </h1>
            <p style={{ fontSize: 11, color: "#666", margin: "2px 0 0 0" }}>
              Continue learning, {user?.full_name?.split(" ")[0] || "there"}
            </p>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 4, borderBottom: "0.5px solid #1c1c1c", marginBottom: 16, alignItems: "center" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "8px 14px", fontSize: 12,
            background: "transparent", border: "none", cursor: "pointer",
            borderBottom: tab === t.key ? `2px solid ${NEON}` : "2px solid transparent",
            color: tab === t.key ? NEON : "#888",
            marginBottom: -1, display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <t.Icon size={12} /> {t.label}
          </button>
        ))}
        {isManager && (
          <button
            onClick={() => navigate("/messenger/learning/team")}
            style={{
              marginLeft: "auto",
              padding: "8px 16px",
              borderRadius: 6,
              background: "rgba(0,255,153,0.08)",
              border: "1px solid rgba(0,255,153,0.2)",
              color: NEON,
              fontSize: 13,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            👥 Team Learning →
          </button>
        )}
      </div>

      {tab === "my"      && <MyLearningTab user={user} />}
      {tab === "library" && <LibraryTab />}
      {tab === "certs"   && <CertificatesTab />}
      {tab === "skills"  && <SkillsTab />}
    </div>
  );

  return embedded ? inner : <MessengerLayout>{inner}</MessengerLayout>;
}

// ──────────────────────────────────────────────────────────────────────
// TAB 1 — My Learning
// ──────────────────────────────────────────────────────────────────────
function MyLearningTab({ user }) {
  const navigate = useNavigate();
  const [enrol, setEnrol] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API_BASE}/api/learning/my-courses`, { headers: authHeaders() });
    if (r.ok) setEnrol(await r.json());
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const inProgress = enrol.filter((e) => e.status === "in_progress" || e.status === "enrolled");
  const completed  = enrol.filter((e) => e.status === "completed");
  const overdue    = enrol.filter((e) => e.due_date && e.status !== "completed" &&
                                          new Date(e.due_date) < new Date());

  if (loading) return <p style={{ color: "#666", fontSize: 12 }}>Loading…</p>;

  return (
    <div style={{ display: "grid", gap: 18 }}>
      {overdue.length > 0 && (
        <Section title="Overdue mandatory" color={RED}>
          <Row>{overdue.map((e) => <CourseMiniCard key={e.id} enr={e} kind="overdue" onOpen={() => navigate(`/messenger/learning/course/${e.course_id}`)} />)}</Row>
        </Section>
      )}
      <Section title="In progress" color={NEON}>
        {inProgress.length === 0 ? <Empty text="Nothing in progress." /> :
          <Row>{inProgress.map((e) => <CourseMiniCard key={e.id} enr={e} onOpen={() => navigate(`/messenger/learning/course/${e.course_id}`)} />)}</Row>}
      </Section>
      <Section title="Completed" color="#888">
        {completed.length === 0 ? <Empty text="No completions yet." /> :
          <Row>{completed.map((e) => <CourseMiniCard key={e.id} enr={e} kind="done" onOpen={() => navigate(`/messenger/learning/course/${e.course_id}`)} />)}</Row>}
      </Section>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// TAB 2 — Course Library
// ──────────────────────────────────────────────────────────────────────
function LibraryTab() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const [activeDiff, setActiveDiff] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (activeCat !== "All") qs.set("category", activeCat);
    if (activeDiff !== "All") qs.set("difficulty", activeDiff.toLowerCase());
    const r = await fetch(`${API_BASE}/api/learning/courses?${qs}`, { headers: authHeaders() });
    if (r.ok) setCourses(await r.json());
    const c = await fetch(`${API_BASE}/api/learning/categories`, { headers: authHeaders() });
    if (c.ok) setCats(await c.json());
    setLoading(false);
  }, [search, activeCat, activeDiff]);
  useEffect(() => { load(); }, [load]);

  const enrol = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/api/learning/courses/${id}/enrol`, {
        method: "POST", headers: authHeaders(),
      });
      if (!r.ok) throw new Error("Enrolment failed");
      toast.success("Enrolled — happy learning!");
      load();
    } catch (e) {
      toast.error("Enrolment failed");
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div style={{ position: "relative", flex: "0 1 280px" }}>
          <Search size={12} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#555" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search courses…"
            style={{ ...inp, paddingLeft: 26 }} />
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {["All", ...cats.map((c) => c.name)].map((n) => (
            <button key={n} onClick={() => setActiveCat(n)} style={pill(activeCat === n)}>{n}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
          {["All", "Beginner", "Intermediate", "Advanced"].map((d) => (
            <button key={d} onClick={() => setActiveDiff(d)} style={pill(activeDiff === d)}>{d}</button>
          ))}
        </div>
      </div>

      {loading ? <p style={{ color: "#666" }}>Loading…</p> :
        courses.length === 0 ? <Empty text="No courses match your filters." /> :
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {courses.map((c) => (
            <CourseBigCard key={c.id} c={c}
              onEnrol={() => enrol(c.id)}
              onOpen={() => navigate(`/messenger/learning/course/${c.id}`)} />
          ))}
        </div>}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// TAB 3 — Certificates
// ──────────────────────────────────────────────────────────────────────
function CertificatesTab() {
  const [certs, setCerts] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/learning/certificates`, { headers: authHeaders() });
    if (r.ok) setCerts(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: "#666" }}>{certs.length} certificate{certs.length === 1 ? "" : "s"}</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => setUploadOpen(true)} style={btnPrimary}>
          <Upload size={12} /> Upload external certificate
        </button>
      </div>

      {certs.length === 0 ? <Empty text="No certificates yet — complete a course to earn one." /> :
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
          {certs.map((c) => <CertificateCard key={c.id} c={c} />)}
        </div>}

      {uploadOpen && <UploadCertModal onClose={() => setUploadOpen(false)} onDone={() => { setUploadOpen(false); load(); }} />}
    </div>
  );
}

function CertificateCard({ c }) {
  const expiryColor = c.expired ? RED : c.expiring_soon ? AMBER : NEON;
  const expiryLabel = c.expired ? "Expired" :
                      c.expiring_soon ? "Expiring soon" :
                      c.expires_at ? "Valid" : "Lifetime";

  const download = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/learning/certificates/${c.id}/download`, {
        headers: authHeaders(),
      });
      if (!res.ok) { toast.error("Certificate download failed"); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `certificate_${c.certificate_number || c.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Certificate download failed");
    }
  };

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <Award size={16} color={expiryColor} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>{c.title}</div>
          <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
            {c.is_external ? `External · ${c.external_issuer}` : "Issued by KweryAI"}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", marginTop: 10, gap: 6 }}>
        <span style={badge(expiryColor)}>{expiryLabel}</span>
        {c.score != null && <span style={{ fontSize: 10, color: "#888" }}>Score: {c.score}%</span>}
      </div>
      <div style={{ fontSize: 10, color: "#555", marginTop: 4 }}>
        Issued {c.issued_at?.slice(0, 10) || "—"}{c.expires_at ? ` · Expires ${c.expires_at.slice(0, 10)}` : ""}
      </div>
      <button onClick={download}
        style={{ ...btnOutline, marginTop: 10, display: "inline-flex" }}>
        <Download size={11} /> Download
      </button>
    </div>
  );
}

function UploadCertModal({ onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [issuer, setIssuer] = useState("");
  const [issued, setIssued] = useState("");
  const [expires, setExpires] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !title || !issuer || !issued) { setErr("Title, issuer, issue date and file are required"); return; }
    setBusy(true); setErr(null);
    const fd = new FormData();
    fd.append("title", title); fd.append("issuer", issuer);
    fd.append("issued_at", issued);
    if (expires) fd.append("expires_at", expires);
    fd.append("file", file);
    try {
      const r = await fetch(`${API_BASE}/api/learning/certificates/upload`, {
        method: "POST", headers: authHeaders(), body: fd,
      });
      if (!r.ok) throw new Error(await r.text());
      onDone();
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  return (
    <div style={modalBg}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} style={modalCard}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <strong style={{ color: "#fff", fontSize: 14 }}>Upload external certificate</strong>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose} style={iconBtn}><X size={14} /></button>
        </div>
        <Field label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} style={inp} /></Field>
        <Field label="Issuer"><input value={issuer} onChange={(e) => setIssuer(e.target.value)} style={inp} /></Field>
        <Field label="Issued (YYYY-MM-DD)"><input type="date" value={issued} onChange={(e) => setIssued(e.target.value)} style={inp} /></Field>
        <Field label="Expires (optional)"><input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} style={inp} /></Field>
        <Field label="File (PDF / image)">
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            onChange={(e) => setFile(e.target.files?.[0])} style={{ ...inp, padding: 4 }} />
        </Field>
        {err && <p style={{ color: RED, fontSize: 11 }}>⚠ {err}</p>}
        <button type="submit" disabled={busy} style={{ ...btnPrimary, marginTop: 10 }}>
          {busy ? "Uploading…" : "Upload"}
        </button>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// TAB 4 — Skills
// ──────────────────────────────────────────────────────────────────────
function SkillsTab() {
  const [skills, setSkills] = useState([]);
  const [editing, setEditing] = useState(null); // skill obj

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/learning/skills`, { headers: authHeaders() });
    if (r.ok) setSkills(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const assess = async (level) => {
    if (!editing) return;
    try {
      const r = await fetch(`${API_BASE}/api/learning/skills/${editing.id}/assess`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ level }),
      });
      if (!r.ok) throw new Error("Failed");
      toast.success("Self-assessment saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Could not save assessment");
    }
  };

  const LEVELS = [
    { key: "beginner",   label: "Beginner",   color: "#555" },
    { key: "developing", label: "Developing", color: BLUE },
    { key: "proficient", label: "Proficient", color: NEON },
    { key: "expert",     label: "Expert",     color: PURP },
  ];

  return (
    <div>
      <p style={{ fontSize: 11, color: "#666", margin: "0 0 12px 0" }}>
        Self-assess your skills. Verified entries (badge) were confirmed by your manager.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
        {skills.map((s) => {
          const lvl = LEVELS.find((l) => l.key === s.my_level);
          return (
            <div key={s.id} style={card}>
              <div style={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>{s.name}</div>
              <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>{s.category || "—"}</div>
              <div style={{ display: "flex", alignItems: "center", marginTop: 10, gap: 6 }}>
                {lvl ? (
                  <span style={badge(lvl.color)}>{lvl.label}</span>
                ) : (
                  <span style={{ fontSize: 11, color: "#555" }}>Not assessed</span>
                )}
                {s.verified_by && <CheckCircle2 size={11} color={NEON} title="Verified by manager" />}
              </div>
              <button onClick={() => setEditing(s)} style={{ ...btnOutline, marginTop: 8, fontSize: 10 }}>
                Self-assess
              </button>
            </div>
          );
        })}
      </div>

      {editing && (
        <div style={modalBg} onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} style={modalCard}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
              <strong style={{ color: "#fff", fontSize: 14 }}>{editing.name}</strong>
              <div style={{ flex: 1 }} />
              <button onClick={() => setEditing(null)} style={iconBtn}><X size={14} /></button>
            </div>
            <p style={{ fontSize: 11, color: "#888", margin: "0 0 10px 0" }}>How would you rate yourself?</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {LEVELS.map((l) => (
                <button key={l.key} onClick={() => assess(l.key)} style={{
                  padding: 10, borderRadius: 8, background: "rgba(255,255,255,0.02)",
                  border: `0.5px solid ${l.color}55`, color: l.color, textAlign: "left",
                  cursor: "pointer", fontSize: 12,
                }}>{l.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Small shared components
// ──────────────────────────────────────────────────────────────────────
function Section({ title, color, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: color || "#888", textTransform: "uppercase",
                   letterSpacing: 0.5, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}
function Row({ children }) {
  return (
    <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
      {children}
    </div>
  );
}
function Empty({ text }) {
  return (
    <div style={{ padding: 20, border: "0.5px dashed #2a2a2a", borderRadius: 10,
                 textAlign: "center", color: "#555", fontSize: 12 }}>
      {text}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function CourseMiniCard({ enr, kind, onOpen }) {
  const color = kind === "overdue" ? RED : kind === "done" ? NEON : BLUE;
  return (
    <div style={{ ...card, minWidth: 240, flexShrink: 0 }}>
      <div style={{ height: 4, background: color, borderRadius: 2, marginBottom: 8 }} />
      <div style={{ fontSize: 12, color: "#fff", fontWeight: 500 }}>{enr.course_title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
        {kind === "done" && <span style={badge(NEON)}>Completed ✓</span>}
        {kind === "overdue" && <span style={badge(RED)}>OVERDUE</span>}
        {enr.is_mandatory && kind !== "done" && <span style={badge(AMBER)}>Mandatory</span>}
      </div>
      <ProgressBar value={enr.progress_pct || (enr.status === "completed" ? 100 : 0)} />
      <div style={{ display: "flex", alignItems: "center", marginTop: 8, gap: 6 }}>
        {enr.due_date && <span style={{ fontSize: 10, color: "#666" }}><Clock size={10} /> Due {enr.due_date}</span>}
        <div style={{ flex: 1 }} />
        <button onClick={onOpen} style={btnOutline}>
          {kind === "done" ? "Review" : "Continue"} <ChevronRight size={11} />
        </button>
      </div>
    </div>
  );
}

function CourseBigCard({ c, onEnrol, onOpen }) {
  return (
    <div style={card}>
      <div style={{ height: 6, background: c.category_color || BLUE, borderRadius: 3, marginBottom: 8 }} />
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>{c.title}</div>
          <p style={{ fontSize: 11, color: "#888", margin: "4px 0 0 0", overflow: "hidden",
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
            {c.description}
          </p>
        </div>
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 10 }}>
        {c.is_mandatory && <span style={badge(RED)}>Mandatory</span>}
        <span style={badge("#666")}><Clock size={9} /> {c.duration_minutes}m</span>
        <span style={badge(BLUE)}>{c.difficulty}</span>
        {(c.skills_awarded || []).slice(0, 3).map((s) => <span key={s} style={badge(NEON)}>{s}</span>)}
      </div>
      <div style={{ marginTop: 10 }}>
        {c.enrolment_status === "completed" ? (
          <button onClick={onOpen} style={{ ...btnOutline, width: "100%", justifyContent: "center" }}>Completed ✓</button>
        ) : c.is_enrolled ? (
          <button onClick={onOpen} style={{ ...btnPrimary, width: "100%", justifyContent: "center" }}>
            Continue · {c.progress_pct}%
          </button>
        ) : (
          <button onClick={onEnrol} style={{ ...btnPrimary, width: "100%", justifyContent: "center" }}>
            Enrol
          </button>
        )}
      </div>
    </div>
  );
}

function ProgressBar({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div style={{ height: 5, background: "#1a1a1a", borderRadius: 3, marginTop: 8, overflow: "hidden" }}>
      <div style={{ width: `${v}%`, background: NEON, height: "100%" }} />
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────
const card = { background: "#0d0d0d", border: "0.5px solid #1c1c1c", borderRadius: 10, padding: 12 };
const inp = {
  background: "#0a0a0a", border: "0.5px solid #2a2a2a", borderRadius: 8,
  padding: "6px 10px", fontSize: 12, color: "#fff", outline: "none", width: "100%",
};
const pill = (active) => ({
  padding: "4px 10px", borderRadius: 999, fontSize: 11,
  background: active ? "rgba(0,255,153,0.08)" : "transparent",
  border: active ? `0.5px solid ${NEON}55` : "0.5px solid #2a2a2a",
  color: active ? NEON : "#888", cursor: "pointer",
});
const badge = (color) => ({
  display: "inline-flex", alignItems: "center", gap: 3,
  padding: "1px 8px", borderRadius: 4, fontSize: 9,
  background: `${color}1a`, color, fontWeight: 500,
});
const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "6px 12px", fontSize: 11, borderRadius: 8,
  background: `${NEON}1f`, border: `0.5px solid ${NEON}55`,
  color: NEON, cursor: "pointer",
};
const btnOutline = {
  display: "inline-flex", alignItems: "center", gap: 4,
  padding: "5px 10px", fontSize: 10, borderRadius: 6,
  background: "transparent", border: `0.5px solid ${NEON}55`,
  color: NEON, cursor: "pointer",
};
const iconBtn = {
  background: "transparent", border: "0.5px solid #2a2a2a",
  borderRadius: 8, padding: 4, color: "#888", cursor: "pointer",
};
const iconBox = {
  width: 36, height: 36, borderRadius: 10,
  background: "rgba(0,255,153,0.1)", color: NEON,
  display: "flex", alignItems: "center", justifyContent: "center",
  border: `0.5px solid ${NEON}33`,
};
const modalBg = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
  backdropFilter: "blur(4px)", zIndex: 200,
  display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
};
const modalCard = {
  background: "#111", border: "0.5px solid #1c1c1c", borderRadius: 12,
  padding: 18, width: 400, maxWidth: "100%",
};
